import { Spinner, Table, type Column } from "../../components";
import { useRateBudget } from "../../api/hooks";
import type { SystemBudget } from "../../api/types";
import { t } from "../../i18n/t";

/**
 * P5-web §2g, redrawn as PracticeGapsBudget.html's per-system rate budget card: the "configured limits"
 * note, the system, the daily limit, the consumption bar and queued count. The backend's own docstring
 * calls the consumption values placeholders, so the bar is drawn from the value the API returns and the
 * note says what it is.
 */
export function RateBudgetTab({ practiceId }: { practiceId: string }): JSX.Element {
  const budget = useRateBudget(practiceId);

  const columns: readonly Column<SystemBudget>[] = [
    {
      key: "system",
      header: t("practice.budget.col_system"),
      render: (row) => <span className="cellvendor__name">{row.system}</span>,
    },
    {
      key: "limit",
      header: t("practice.budget.col_limit"),
      numeric: true,
      render: (row) => <span className="mono">{String(row.limit)}</span>,
    },
    {
      key: "consumed",
      header: t("practice.budget.col_consumed"),
      render: (row) => {
        const limit = typeof row.limit === "number" && row.limit > 0 ? row.limit : null;
        const ratio = limit === null ? 0 : Math.min(1, row.consumed_today / limit);
        return (
          <span className="cellvendor">
            <span className="bar" aria-hidden="true">
              <span style={{ width: `${Math.round(ratio * 100)}%` }} />
            </span>
            <span className="mono muted">{row.consumed_today === 0 ? "—" : String(row.consumed_today)}</span>
          </span>
        );
      },
    },
    {
      key: "queued",
      header: t("practice.budget.col_queued"),
      numeric: true,
      render: (row) => <span className="mono">{String(row.clients_queued)}</span>,
    },
    {
      key: "projected",
      header: t("practice.budget.col_projected"),
      render: (row) => <span className="mono muted">{row.projected_finish ?? "—"}</span>,
    },
  ];

  return (
    <section className="card card--flush" aria-labelledby="budget-title">
      <div className="card__header">
        <h2 className="card__title" id="budget-title">
          {t("practice.budget.caption")}
        </h2>
        <div role="note" className="budgetnote">
          {t("practice.budget.note")}
        </div>
      </div>
      {budget.isPending ? (
        <Spinner />
      ) : (
        <Table
          bare
          caption={t("practice.budget.caption")}
          columns={columns}
          rows={budget.data?.systems ?? []}
          rowKey={(row) => row.system}
          emptyMessage={t("practice.budget.empty")}
        />
      )}
    </section>
  );
}
